import {useCallback, useContext} from 'react';
import {atom, useAtom} from 'jotai';
import SelectIcon from '@/icons/select-mode.svg';
import PanIcon from '@/icons/pan-mode.svg';
import NewScreenIcon from '@/icons/new-screen.svg';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {cn} from '@features/ui/utils';
import {Zoom} from './Zoom';
import {captureException} from '@sentry/react';
import amplitudeService from '@features/analytics/amplitudeService';
import {useScreenCreationContext} from '../hooks/ScreenCreationContext';
import {isFlowNotReadyError} from '@features/flow/utils/flowNotReady';
import {useSubscription} from '@features/subscription/subscriptionHook';
import {usePaywallModal} from '@features/subscription/PaywallModal';
import {Button} from '@features/ui/components/Button';
import CrownIcon from '@/icons/crown.svg';
import EyeIcon from '@/icons/eye.svg';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {trpc} from '@features/utils/trpc';
import {FlowContext} from '@features/contexts/FlowContext';
import {useChangeFlowVersion} from '../flowHook';
import {useIsMac} from '../hooks/useIsMac';
import Undo from '@/icons/Undo.svg';
import Redo from '@/icons/Redo.svg';
import {useIsFlowBusy} from '@/app/flow/hooks/useIsFlowBusy';

type InteractionMode = 'pan' | 'select';

export const interactionModeAtom = atom<InteractionMode>('select');

export const InteractionModeToggle = () => {
  const [mode, setMode] = useAtom(interactionModeAtom);
  const {createScreenInFreeSpace} = useScreenCreationContext();
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();
  const {readonly} = useContext(ReadonlyContext);
  const flowId = useContext(FlowContext).flow.id;
  const undo = trpc.flow.getUndoStatus.useQuery({flowId}).data;
  const isGenerating = useIsFlowBusy();
  const {mutate: changeFlowVersion} = useChangeFlowVersion();
  const isMac = useIsMac();

  const showUpgrade = !effectiveSubscription?.isActive;

  const changeVersion = useCallback(
    (version: string | null) => {
      if (!version) {
        amplitudeService.logEvent(
          '[Canvas Toolbar] Attempted to change to invalid version (null)',
          {version},
        );
        return;
      } else if (isGenerating) {
        amplitudeService.logEvent(
          '[Canvas Toolbar] Attempted to change version while generation in progress',
          {version},
        );
        return;
      }
      changeFlowVersion({flowId, version});
    },
    [changeFlowVersion, flowId, isGenerating],
  );

  const handleUpgradeClick = () => {
    amplitudeService.logEvent('[Canvas Toolbar] "Upgrade" clicked');
    openPaywallModal('canvas-toolbar');
  };

  const handleModeChange = (newMode: InteractionMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    amplitudeService.logEvent('[Canvas] interaction mode changed', {
      mode: newMode,
      trigger: 'button',
    });
  };

  const handleNewScreen = () => {
    void createScreenInFreeSpace()
      .then(() => {
        amplitudeService.logEvent('[Canvas] new screen from toolbar');
      })
      // The flow-not-ready case is surfaced to the user by the global mutation
      // handler; swallow it here so it isn't an unhandled rejection. Report
      // anything unexpected.
      .catch((error: unknown) => {
        if (!isFlowNotReadyError(error)) captureException(error);
      });
  };

  return (
    <div className='flex h-8 flex-row items-stretch'>
      <div className='flex h-8 flex-row items-stretch gap-1'>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label='Select mode'
              className={cn(
                'flex h-full w-8 items-center justify-center rounded-lg',
                mode === 'select'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:bg-gray-100',
              )}
              onClick={() => handleModeChange('select')}
            >
              <SelectIcon className='h-6 w-6' />
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={8}>
            Select
            <span className='ml-1 text-xs opacity-60'>V</span>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label='Pan mode'
              className={cn(
                'flex h-full w-8 items-center justify-center rounded-lg',
                mode === 'pan'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:bg-gray-100',
              )}
              onClick={() => handleModeChange('pan')}
            >
              <PanIcon className='h-6 w-6' />
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={8}>
            Pan
            <span className='ml-1 text-xs opacity-60'>H or hold Space</span>
          </TooltipContent>
        </Tooltip>
      </div>
      {!readonly && (
        <>
          <div className='-my-2 mx-2 w-px bg-gray-200' />
          <div className='flex h-8 flex-row items-stretch gap-1'>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label='Create new screen'
                  className='flex h-full w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-gray-100'
                  onClick={handleNewScreen}
                >
                  <NewScreenIcon className='h-6 w-6' />
                </button>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>New screen</TooltipContent>
            </Tooltip>
            {undo?.compatible && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label='Undo'
                      className={cn(
                        'flex h-full w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-gray-100',
                        (!undo.undoVersion || isGenerating) &&
                          'cursor-not-allowed opacity-50',
                      )}
                      disabled={!undo.undoVersion || isGenerating}
                      onClick={() => changeVersion(undo.undoVersion)}
                    >
                      <Undo className='h-6 w-6' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={8}>
                    Undo
                    <span className='ml-1 text-xs opacity-60'>
                      {isMac ? '⌘Z' : 'Ctrl+Z'}
                    </span>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label='Redo'
                      className={cn(
                        'flex h-full w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-gray-100',
                        (!undo.redoVersion || isGenerating) &&
                          'cursor-not-allowed opacity-50',
                      )}
                      disabled={!undo.redoVersion || isGenerating}
                      onClick={() => changeVersion(undo.redoVersion)}
                    >
                      <Redo className='h-6 w-6' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={8}>
                    Redo
                    <span className='ml-1 text-xs opacity-60'>
                      {isMac ? '⌘⇧Z' : 'Ctrl+Shift+Z'}
                    </span>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </>
      )}
      <div className='-my-2 mx-2 w-px bg-gray-200' />
      <Zoom />
      {!readonly && showUpgrade && (
        <div className='-my-2 mx-2 w-px bg-gray-200' />
      )}
      {!readonly && showUpgrade && (
        <Button
          size='sm'
          className='w-[100px] justify-center bg-[#ebebeb] py-1 pr-1 text-muted-foreground hover:bg-[#ebebeb]'
          onClick={handleUpgradeClick}
        >
          <span className='flex items-center'>
            Upgrade
            <CrownIcon className='mr-1 h-6 w-6' />
          </span>
        </Button>
      )}
      {readonly && <div className='-my-2 mx-2 w-px bg-gray-200' />}
      {readonly && (
        <div className='flex h-8 w-28 items-center justify-center rounded-lg bg-gray-200 py-1 pl-3 pr-2 text-sm font-semibold text-muted-foreground'>
          View only
          <EyeIcon className='h-6 w-6' />
        </div>
      )}
    </div>
  );
};
